import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";

import { Box, Typography } from "@mui/material";
import { format, isToday } from "date-fns";
import { GroupedVirtuoso, GroupedVirtuosoHandle } from "react-virtuoso";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import {
  AppointmentListItemFragment,
  AppointmentStatusKind,
} from "@/gql/graphql";
import { formatISODate } from "@/utils/datetime";
import { FragmentType } from "@/utils/type";

import AppointmentListItem from "../components/AppointmentListItem";
import { isAppointmentFinished } from "../utils/isAppointmentFinished";

const useStyles = makeStyles()({
  groups: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  list: {
    height: "100%",
  },
  header: {
    paddingTop: 20,
    paddingBottom: 12,
    backgroundColor: "#F5F7FA",
  },
  groupDate: {
    fontSize: 20,
    fontWeight: 700,
  },
  item: {
    marginTop: 5,
  },
  spacer: {
    height: "40vh",
  },
});

interface Props {
  data: FragmentType<typeof appointmentListFragment> | undefined;
  onClickMenu: (type: string, id: string) => void;
  handleShowFinishAppointmentModal: (
    data: AppointmentListItemFragment,
    status: AppointmentStatusKind
  ) => void;
  searchAppointmentId: string | null;
  filterValue: string;
  scrollToDate?: Date;
}

const appointmentListFragment = graphql(`
  fragment AppointmentList on Query {
    appointments {
      date
      appointments {
        ...AppointmentListItem
      }
    }
  }
`);

export interface AppointmentListHandle {
  scrollToDate(date: Date): void;
}

const AppointmentList = forwardRef<AppointmentListHandle, Props>(
  (
    {
      data,
      onClickMenu,
      searchAppointmentId,
      filterValue,
      handleShowFinishAppointmentModal,
    },
    forwardedRef
  ) => {
    const { classes } = useStyles();
    const listRef = useRef<GroupedVirtuosoHandle>(null);

    const filteredAppointments = useMemo(() => {
      const groupedAppointments = data?.appointments ?? [];
      switch (filterValue) {
        case "ALL":
          return groupedAppointments;
        case "FINISHED":
          return groupedAppointments.map((group) => ({
            date: group.date,
            appointments:
              group.appointments?.filter(isAppointmentFinished) ?? [],
          }));
        case "UPCOMING":
          return groupedAppointments.map((group) => ({
            date: group.date,
            appointments:
              group.appointments?.filter(
                (a) => a.status === "upcoming" && !isAppointmentFinished(a)
              ) ?? [],
          }));
        default:
          return groupedAppointments.map((group) => ({
            date: group.date,
            appointments:
              group.appointments?.filter(
                (a) => a.status === filterValue.toLowerCase()
              ) ?? [],
          }));
      }
    }, [data?.appointments, filterValue]);

    const { appointments, groups, groupCounts } = useMemo(() => {
      const appointments: AppointmentListItemFragment[] = [];
      const groups: string[] = [];
      const groupCounts: number[] = [];
      filteredAppointments.forEach((group) => {
        groups.push(group.date);
        groupCounts.push(group.appointments?.length ?? 0);
        appointments.push(...(group.appointments ?? []));
      });
      return { appointments, groups, groupCounts };
    }, [filteredAppointments]);

    const initialScroll = useMemo(() => {
      // Scroll to current date with no animation for first render
      const groupIndex = findNearestDate(groups, new Date());
      return { groupIndex, index: 1, align: "start" as const };
    }, [groups]);

    useImperativeHandle(forwardedRef, () => ({
      scrollToDate(date) {
        listRef.current?.scrollIntoView({
          groupIndex: findNearestDate(groups, date),
          behavior: "smooth",
          align: "start",
        });
      },
    }));

    return (
      <Box className={classes.groups}>
        <GroupedVirtuoso
          ref={listRef}
          className={classes.list}
          components={{
            // Bottom space/padding to make scrolling into current date go to
            // the top when current date is the last group of appointments.
            Footer: () => <div className={classes.spacer} />,
          }}
          groupCounts={groupCounts}
          groupContent={(index) => {
            const date = groups[index];
            const parsedDate = new Date(date);
            return (
              <div key={date} className={classes.header}>
                <Typography className={classes.groupDate}>
                  {format(parsedDate, "dd MMM, yyyy")}
                  {isToday(parsedDate) && " (Today)"}
                </Typography>
              </div>
            );
          }}
          itemContent={(index) => {
            const appointment = appointments[index];
            return (
              <AppointmentListItem
                key={appointment?.id}
                id={appointment?.id}
                className={classes.item}
                onClickMenu={onClickMenu}
                handleShowFinishModal={handleShowFinishAppointmentModal}
              />
            );
          }}
          initialTopMostItemIndex={initialScroll}
        />
      </Box>
    );
  }
);

function findNearestDate(groups: string[], date: Date): number {
  // Find the index of this date in the group. If the date does not exist,
  // we'll scroll to the last date before this date.
  // let groupIndex = 0;
  const dateString = formatISODate(date);
  let groupIndex = 0;
  for (let i = 0; i < groups.length; i++) {
    // Exact date found, end the iteration.
    if (groups[i] === dateString) {
      groupIndex = i;
      break;
    }
    // Groups are sorted from latest to earliest, so if the group date is
    // lesser than the date argument, its previous date will be the
    // nearest date of the date argument.
    if (groups[i] < dateString) {
      groupIndex = Math.max(i - 1, 0);
      break;
    }

    // If there's no matches, this will scroll to the last item.
    groupIndex = i;
  }

  return groupIndex;
}

export default AppointmentList;
